// The V3 ("HTML lomda") theme shape now lives in @yedalabs/schema (it types the
// BrandKitTable.htmlLomdaTheme column). Re-export it so the editor keeps one
// import path.
import type {
    LomdaTheme,
    LomdaBackgroundImage,
    LomdaBackgrounds,
} from "@yedalabs/schema";
export type { LomdaTheme, LomdaBackgroundImage, LomdaBackgrounds };

export type LomdaV3BlockType =
    | "text"
    | "media-layout"
    | "audio"
    | "embed"
    | "html"
    | "attachment"
    | "walkthrough"
    | "accordion"
    | "tabs"
    | "checklist"
    | "flip-cards"
    | "scenario"
    | "card-sorting"
    | "how-to"
    | "hotspot"
    | "knowledge-check"
    | "steps"
    | "columns"
    | "rating"
    | "poll"
    | "word-cloud"
    | "open-ended"
    | "complete-sentence"
    | "chart"
    | "spacer"
    | "divider"
    | "button";

export type TextVariant =
    | "text"
    | "heading-text"
    | "multi-columns"
    // Deprecated — kept so previously saved blocks still render.
    | "two-columns"
    | "three-columns"
    | "quote"
    | "link"
    | "table";

export interface TextColumn {
    heading: string;
    text: string;
}

export type QuoteAlign = "start" | "center";

/** Block-level appearance for the table text-variant. */
export interface TableAppearance {
    cardBackground: string;
    border: string;
    borderWidth: number;
    headerBackground: string;
}

export type TextData =
    | { variant: "text"; html: string }
    | { variant: "heading-text"; heading: string; text: string }
    | { variant: "multi-columns"; columns: TextColumn[] }
    | { variant: "two-columns"; columns: TextColumn[] }
    | { variant: "three-columns"; columns: TextColumn[] }
    | {
          variant: "quote";
          quote: string;
          authorName: string;
          authorTitle: string;
          authorPhoto: string;
          showAuthorPhoto: boolean;
          align: QuoteAlign;
      }
    | {
          variant: "link";
          url: string;
          title: string;
          description: string;
          thumbnail: string;
      }
    | {
          variant: "table";
          /** Structured grid: rows of cell HTML. */
          rows?: string[][];
          /** First row renders as a header (`<th>`). */
          headerRow?: boolean;
          /** Legacy single-blob HTML table (migrated to `rows` on first edit). */
          html?: string;
          appearance?: TableAppearance;
      };

/**
 * A background painted BEHIND a text block. Unlike the appearance objects on
 * other blocks this one is a band: by default it leaves the centred content
 * column and fills the page, while the text itself stays where it was.
 */
export interface TextBackground {
    color?: string;
    /** Second gradient stop. Set = the band is a gradient from `color` to this;
     *  unset = `color` is a flat fill. Keeps old flat backgrounds working. */
    colorTo?: string;
    /** CSS gradient angle in degrees (180 = downwards). */
    gradientAngle?: number;
    imageUrl?: string;
    /** 0-1 dark layer over the image, so the text stays readable on a photo. */
    overlay?: number;
    /** false = the background stays inside the content column. */
    fullWidth?: boolean;
    /**
     * Band height in px, as a MINIMUM — long text still grows the band rather
     * than being clipped. Unset = the band is exactly as tall as its text.
     * Deliberately not an aspect ratio: this band is as wide as the page, so a
     * ratio would be a modest banner on a phone and a 900px wall on a monitor.
     */
    minHeight?: number;
    /**
     * Fill the visible page area — a cover/hero band. Not `100vh`: the preview
     * spends part of the window on its header and navigation, so the viewport
     * is taller than the page the learner actually sees.
     */
    fullHeight?: boolean;
    /** How the image fills the band. Same modes as the image block. */
    fill?: ImageFill;
    /**
     * Which part of the image survives the crop, as percentages (50/50 =
     * centre). A wide, short band crops a photo hard, and the interesting part
     * is rarely the middle.
     */
    focalX?: number;
    focalY?: number;
    /**
     * Where the text sits on the band, as percentages of the band's box (the
     * text's top-start corner). Percentages, not pixels, so the placement holds
     * as the band changes width. Only meaningful once the band has a designed
     * height (Fixed/Full) — an auto band hugs its text, leaving nowhere to move
     * to — and dropped entirely on narrow screens, where the text returns to
     * normal flow instead of covering the picture.
     */
    textX?: number;
    textY?: number;
}

export interface TextBlock {
    id: string;
    type: "text";
    data: TextData;
    background?: TextBackground;
}

export type ImageWidth = "sm" | "md" | "lg";
// `fill`/`fit`/`crop` are the Figma modes; `cover`/`contain` are kept for
// backward compatibility (cover↔crop, contain↔fit).
export type ImageFill = "fill" | "fit" | "crop" | "cover" | "contain";
export type MediaAspect = "auto" | "16:9" | "4:3" | "1:1";
export type MediaKind = "image" | "video";
export type MediaPosition = "none" | "top" | "bottom" | "start" | "end";

export interface MediaCell {
    url: string;
    alt: string;
    videoId: string;
    name: string;
    thumbnailUrl: string;
    /** User-uploaded thumbnail/poster that overrides the auto-generated one. */
    customThumbnailUrl?: string;
    heading: string;
    text: string;
}

export interface MediaLayoutBlock {
    id: string;
    type: "media-layout";
    data: {
        kind: MediaKind;
        position: MediaPosition;
        /** Width as a percentage of the content area (legacy: sm/md/lg). */
        width: number;
        aspect: MediaAspect;
        fill: ImageFill;
        cells: MediaCell[];
    };
}

export interface AudioBlock {
    id: string;
    type: "audio";
    data: { url: string; name: string };
}

export type EmbedProvider =
    | "storage"
    | "web-link"
    | "canva"
    | "microsoft"
    | "google"
    | "surveymonkey"
    | "vimeo"
    | "slideshare"
    | "prezi"
    | "youtube"
    | "powtoon"
    | "h5p";

export interface EmbedBlock {
    id: string;
    type: "embed";
    data: { provider: EmbedProvider; url: string };
}

export interface HtmlBlock {
    id: string;
    type: "html";
    data: { html: string };
}

export interface AttachmentBlock {
    id: string;
    type: "attachment";
    data: { url: string; name: string; size: number };
}

export interface WalkthroughButton {
    id: string;
    text: string;
    linkStepId: string;
}

export type HotspotTextPosition =
    | "auto"
    | "top"
    | "bottom"
    | "start"
    | "end"
    | "none";

export interface WalkthroughHotspot {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label: string;
    color: string;
    textPosition: HotspotTextPosition;
    linkStepId: string;
}

export interface WalkthroughInfoStep {
    id: string;
    type: "info";
    name: string;
    heading: string;
    text: string;
    buttons: WalkthroughButton[];
}

export interface WalkthroughImageStep {
    id: string;
    type: "image";
    name: string;
    heading: string;
    image: string;
    alt: string;
    aspect: MediaAspect;
    fit: ImageFill;
    /** true = learner must click a highlight to continue; false = info only, advance with a button. */
    interactive: boolean;
    hotspots: WalkthroughHotspot[];
}

export type WalkthroughStep = WalkthroughInfoStep | WalkthroughImageStep;
export type WalkthroughStepType = WalkthroughStep["type"];

export interface WalkthroughBlock {
    id: string;
    type: "walkthrough";
    data: { steps: WalkthroughStep[] };
}

export interface AccordionItem {
    id: string;
    title: string;
    body: string;
}

/** Block-level appearance for accordion (applies to all items). */
export interface AccordionAppearance {
    cardBackground: string;
    border: string;
    borderWidth: number;
    showCheckmark: boolean;
}

export interface AccordionBlock {
    id: string;
    type: "accordion";
    data: {
        title: string;
        description: string;
        items: AccordionItem[];
        appearance?: AccordionAppearance;
    };
}

/** Per-tab overrides; unset falls back to the block appearance / theme. */
export interface TabItemAppearance {
    /** This tab's label + icon colour (active and inactive alike). */
    textColor?: string;
}

export interface TabItem {
    id: string;
    label: string;
    icon: string;
    appearance?: TabItemAppearance;
    blocks: LomdaV3Block[];
    /** @deprecated replaced by `blocks`. */
    content?: string;
    /** @deprecated replaced by `blocks`. */
    body?: string;
}

/** Block-level appearance for the tabs block. */
export interface TabsAppearance {
    tabBackground: string;
    activeTabBackground: string;
    border: string;
    borderWidth: number;
    showIcons: boolean;
}

export interface TabsBlock {
    id: string;
    type: "tabs";
    data: { intro: string; items: TabItem[]; appearance?: TabsAppearance };
}

export interface ChecklistItem {
    id: string;
    text: string;
}

/** Block-level appearance for checklist (applies to all items). */
export interface ChecklistAppearance {
    cardBackground: string;
    border: string;
    borderWidth: number;
}

export interface ChecklistBlock {
    id: string;
    type: "checklist";
    data: {
        title: string;
        items: ChecklistItem[];
        appearance?: ChecklistAppearance;
    };
}

/** Per-card overrides of the block appearance; unset fields fall back to the
 *  block's appearance, then to the theme. Applies to BOTH faces of the card. */
export interface FlipCardAppearance {
    cardBackground?: string;
    /** Image URL painted behind the card. */
    backgroundImage?: string;
    /** 0-1 dark overlay over that image, for text readability (same idea as
     *  the lomda background's scrim). Absent = no overlay. */
    scrim?: number;
    /** Text colour on BOTH faces (e.g. white over a dark background). */
    textColor?: string;
}

export interface FlipCard {
    id: string;
    icon: string;
    appearance?: FlipCardAppearance;
    title: string;
    subtitle: string;
    back: string;
    /** @deprecated replaced by icon/title/subtitle — kept so old cards render. */
    front?: string;
}

/** Block-level appearance for flip-cards (applies to all cards). */
export interface FlipCardsAppearance {
    cardBackground: string;
    iconBackground: string;
    border: string;
    borderWidth: number;
    showCheckmark: boolean;
}

export const FLIP_CARDS_VARIANTS = ["flip", "static"] as const;
export type FlipCardsVariant = (typeof FLIP_CARDS_VARIANTS)[number];

export interface FlipCardsBlock {
    id: string;
    type: "flip-cards";
    data: {
        title: string;
        description: string;
        cards: FlipCard[];
        columns?: number;
        appearance?: FlipCardsAppearance;
        /** Absent = "flip". */
        variant?: FlipCardsVariant;
    };
}

export type ScenarioQuality = "good" | "average" | "poor";

export interface ScenarioChoice {
    id: string;
    text: string;
    feedback: string;
    quality: ScenarioQuality;
    /** Scene to jump to after this choice. "" = next in order, "end" = finish. */
    nextSceneId: string;
}

export interface ScenarioScene {
    id: string;
    name: string;
    dialogue: string;
    choices: ScenarioChoice[];
}

export interface ScenarioBlock {
    id: string;
    type: "scenario";
    data: { scenes: ScenarioScene[] };
}

export interface SortCategory {
    id: string;
    title: string;
}

export interface SortCard {
    id: string;
    text: string;
    categoryId: string;
}

export interface CardSortingBlock {
    id: string;
    type: "card-sorting";
    data: {
        title: string;
        description: string;
        categories: SortCategory[];
        cards: SortCard[];
        /** Cards-grid styling (optional; absent = sensible defaults). */
        columns?: number;
        gap?: number;
        cardBackground?: string;
        cardBorderColor?: string;
        cardBorderWidth?: number;
    };
}

export interface HowToStep {
    id: string;
    title: string;
    body: string;
    image: string;
}

export interface HowToBlock {
    id: string;
    type: "how-to";
    data: { steps: HowToStep[] };
}

export interface HotspotMarker {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label: string;
    color: string;
    textPosition: HotspotTextPosition;
}

export interface HotspotBlock {
    id: string;
    type: "hotspot";
    data: {
        image: string;
        alt: string;
        aspect: MediaAspect;
        fit: ImageFill;
        markers: HotspotMarker[];
    };
}

export interface QuizOption {
    id: string;
    text: string;
    correct: boolean;
    /** Shown after Check under a picked option and under every correct one. */
    feedback?: string;
}

export type QuizQuestionType =
    | "single"
    | "multiple"
    | "true-false"
    | "open";

export interface QuizQuestion {
    id: string;
    text: string;
    multiple: boolean;
    /** Question type. Optional for back-compat; derive with `quizType()`. */
    type?: QuizQuestionType;
    explanation: string;
    options: QuizOption[];
    /** Custom instruction line; unset or blank = the per-type default. */
    instruction?: string;
    hideInstruction?: boolean;
}

/** Block-level appearance for a knowledge-check (applies to all questions). */
export interface QuizAppearance {
    /** Only the rows the author changed are stored; unset = theme Card / Border / 1px. */
    background?: string;
    border?: string;
    borderWidth?: number;
    /** Cards after Check. Unset = the theme Success / Destructive tint. */
    correctBackground?: string;
    correctBorder?: string;
    wrongBackground?: string;
    wrongBorder?: string;
}

/** The editor's view: the three base fields filled from the theme. */
export type ResolvedQuizAppearance = QuizAppearance &
    Required<Pick<QuizAppearance, "background" | "border" | "borderWidth">>;

export interface KnowledgeCheckBlock {
    id: string;
    type: "knowledge-check";
    data: {
        title: string;
        description: string;
        questions: QuizQuestion[];
        appearance?: QuizAppearance;
    };
}

export interface StepItem {
    id: string;
    title: string;
    subtitle: string;
    contentHeading: string;
    blocks: LomdaV3Block[];
    /** @deprecated replaced by `blocks`. */
    content?: string;
}

/** Block-level appearance for the steps block. Number text color comes from the
 * theme (primary); only the badge backgrounds are overridable. */
export interface StepsAppearance {
    cardBackground: string;
    border: string;
    borderWidth: number;
    numberBackground: string;
    activeNumberBackground: string;
}

/** "panel" (default) = list + the open step's content; "list" = the list only. */
export const STEPS_DISPLAYS = ["panel", "list"] as const;
export type StepsDisplay = (typeof STEPS_DISPLAYS)[number];

export interface StepsBlock {
    id: string;
    type: "steps";
    data: {
        intro: string;
        items: StepItem[];
        appearance?: StepsAppearance;
        display?: StepsDisplay;
    };
}

export type ColumnVerticalAlign = "top" | "center" | "bottom";

export interface LayoutColumn {
    id: string;
    blocks: LomdaV3Block[];
    /** Width as a percentage of the row (proportional, not pixels). Absent = equal share. */
    width?: number;
    /** Absent = top. */
    verticalAlign?: ColumnVerticalAlign;
}

export interface ColumnsBlock {
    id: string;
    type: "columns";
    data: { columns: LayoutColumn[] };
}

export interface SpacerBlock {
    id: string;
    type: "spacer";
    data: { height: number };
}

export const DIVIDER_THICKNESSES = ["thin", "medium", "thick"] as const;
export type DividerThickness = (typeof DIVIDER_THICKNESSES)[number];

export const DIVIDER_SPACINGS = ["none", "s", "m", "l"] as const;
export type DividerSpacing = (typeof DIVIDER_SPACINGS)[number];

export interface DividerAppearance {
    thickness?: DividerThickness;
    spacing?: DividerSpacing;
    /** Absent = the theme's Border. */
    color?: string;
}

export interface DividerBlock {
    id: string;
    type: "divider";
    data: { appearance?: DividerAppearance };
}

export const BUTTON_ACTIONS = ["url", "page"] as const;
export type ButtonAction = (typeof BUTTON_ACTIONS)[number];

export const BUTTON_STYLES = ["filled", "outline"] as const;
export type ButtonStyle = (typeof BUTTON_STYLES)[number];

export const BUTTON_SIZES = ["s", "m", "l"] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

export const BUTTON_ALIGNS = ["start", "center", "end"] as const;
export type ButtonAlign = (typeof BUTTON_ALIGNS)[number];

export interface ButtonAppearance {
    style?: ButtonStyle;
    size?: ButtonSize;
    align?: ButtonAlign;
    /** Absent = the theme's Primary. */
    color?: string;
    /** Absent = Primary foreground when filled, the button colour when outlined. */
    textColor?: string;
}

export interface ButtonBlock {
    id: string;
    type: "button";
    data: {
        /** Stored as written; never follows a later change of the lomda's language. */
        label?: string;
        action: ButtonAction;
        url?: string;
        pageId?: string;
        appearance?: ButtonAppearance;
    };
}

/** A 1..max rating (e.g. 5 stars). Demo: not persisted — the learner just picks
 * a value and sees a thank-you. */
export interface RatingBlock {
    id: string;
    type: "rating";
    data: {
        question: string;
        /** Highest rating (default 5). */
        max: number;
    };
}

export interface PollOption {
    id: string;
    text: string;
}

/** Block-level appearance for the poll block (option cards + letter badge). */
export interface PollAppearance {
    cardBackground: string;
    optionBackground: string;
    border: string;
    borderWidth: number;
}

export interface PollBlock {
    id: string;
    type: "poll";
    data: {
        question: string;
        options: PollOption[];
        appearance?: PollAppearance;
    };
}

export interface WordCloudBlock {
    id: string;
    type: "word-cloud";
    data: {
        question: string;
        /** How many words the learner enters (default 3). */
        wordCount: number;
    };
}

/** Free-text question; a real AI endpoint returns feedback. Not persisted. */
export interface OpenEndedBlock {
    id: string;
    type: "open-ended";
    data: {
        question: string;
        /** Author guidance to the bot on how to give feedback. */
        guidance: string;
    };
}

/** The three chart shapes: comparison (bar), change over time (line), and
 *  parts of a whole (donut). One data series each — deliberately. */
export type ChartVariant = "bar" | "line" | "donut";

export interface ChartPoint {
    id: string;
    label: string;
    value: number;
    /** Donut only: this slice's colour. Unset follows the theme palette. */
    color?: string;
}

/** Block-level appearance for the chart block. */
export interface ChartAppearance {
    /** Bar/line series colour. Unset = the theme's primary. */
    seriesColor: string;
    cardBackground: string;
    border: string;
    borderWidth: number;
    /** Print each point's value on the chart. */
    showValues: boolean;
    /** Bar only: lay the bars on their side (better for long RTL labels). */
    horizontal: boolean;
}

export interface ChartBlock {
    id: string;
    type: "chart";
    data: {
        variant: ChartVariant;
        title: string;
        /** One line under the chart: what it shows + where the numbers are
         *  from. The AI must not emit a chart it cannot attribute here. */
        caption: string;
        /** Appended to every value ("%", "₪", "hours"). May be empty. */
        unit: string;
        points: ChartPoint[];
        appearance?: ChartAppearance;
    };
}

/** Fill-in-the-blank. `text` contains one `___` marker; the learner's input is
 * checked against `answers` (case/space-insensitive). Self-check, no backend. */
export interface CompleteSentenceBlock {
    id: string;
    type: "complete-sentence";
    data: {
        text: string;
        /** Distractors per blank, indexed by the blank's position in `text`
         *  (blank i = the i-th `_word_`). Each blank's dropdown = its answer +
         *  its own distractors. */
        answers: string[][];
        explanation: string;
    };
}

export type LomdaV3Block =
    | TextBlock
    | MediaLayoutBlock
    | AudioBlock
    | EmbedBlock
    | HtmlBlock
    | AttachmentBlock
    | WalkthroughBlock
    | AccordionBlock
    | TabsBlock
    | ChecklistBlock
    | FlipCardsBlock
    | ScenarioBlock
    | CardSortingBlock
    | HowToBlock
    | HotspotBlock
    | KnowledgeCheckBlock
    | StepsBlock
    | ColumnsBlock
    | RatingBlock
    | PollBlock
    | WordCloudBlock
    | OpenEndedBlock
    | CompleteSentenceBlock
    | ChartBlock
    | SpacerBlock
    | DividerBlock
    | ButtonBlock;

export type LomdaV3BlockData = LomdaV3Block["data"];

export type LomdaV3BlockPatch = Partial<{
    html: string;
    rows: string[][];
    headerRow: boolean;
    heading: string;
    text: string;
    columns: TextColumn[];
    quote: string;
    authorName: string;
    authorTitle: string;
    authorPhoto: string;
    showAuthorPhoto: boolean;
    align: QuoteAlign;
    url: string;
    title: string;
    description: string;
    thumbnail: string;
    alt: string;
    name: string;
    size: number;
    kind: MediaKind;
    position: MediaPosition;
    width: number;
    aspect: MediaAspect;
    fill: ImageFill;
    cells: MediaCell[];
    provider: EmbedProvider;
    steps: WalkthroughStep[];
    intro: string;
    items: TabItem[];
    appearance: TableAppearance;
}>;

export interface PageBackground {
    color: string;
    /** 0–1; absent = 1. */
    opacity?: number;
}

export interface LomdaV3Page {
    id: string;
    order: number;
    title?: string;
    blocks: LomdaV3Block[];
    /** Absent = the lomda's own background. */
    background?: PageBackground;
}

export type BlockThemeOverride = Partial<
    Pick<
        LomdaTheme,
        | "background"
        | "foreground"
        | "primary"
        | "primaryForeground"
        | "card"
        | "cardForeground"
    >
>;

export interface LomdaV3Content {
    pages: LomdaV3Page[];
    /** Optional per-block color overrides, keyed by block id. */
    blockThemes?: Record<string, BlockThemeOverride>;
    /** Vertical spacing (px) between top-level sections/blocks on a page. */
    blockGap?: number;
    /** Default spacing (px) between items inside blocks (grids/lists). */
    gridGap?: number;
    /** Spacing (px) inside one section (title/paragraph/button/image/etc.). */
    innerGap?: number;
    /** Per-block grid-gap override (px), keyed by block id. */
    blockGridGaps?: Record<string, number>;
    /** Per-block width as a percentage of the content area, keyed by block id. */
    blockWidths?: Record<string, number>;
}

export interface LomdaV3BrandStyle {
    titlesFontFamily?: string;
    titlesFontUrl?: string | null;
    bodyFontFamily?: string;
    bodyFontUrl?: string | null;
    customFonts?: { family: string; url: string }[];
    textColor?: string | null;
    lomdaBackgroundColor?: string | null;
    primaryButtonBackgroundColor?: string | null;
    primaryButtonTextColor?: string | null;
}

/** Per-text-preset font weights (platform-side; designer tuning). */
export interface LomdaV3FontWeights {
    h1?: number;
    h2?: number;
    h3?: number;
    h4?: number;
    h5?: number;
    h6?: number;
    label?: number;
    p?: number;
    small?: number;
}

/** Per-text-preset line heights, stored as a percentage (e.g. 116 = 116%). */
export interface LomdaV3LineHeights {
    h1?: number;
    h2?: number;
    h3?: number;
    h4?: number;
    h5?: number;
    h6?: number;
    label?: number;
    p?: number;
    small?: number;
}

export type LomdaV3AssetType = "image" | "video" | "audio" | "file";

/** A file uploaded in this lomda. Stays in the media library after it leaves the page. */
export interface LomdaV3Asset {
    url: string;
    type: LomdaV3AssetType;
    name: string;
    uploadedAt: string;
    // Bytes. Missing on uploads recorded before it was added.
    size?: number;
}

/** How the learner moves through the lomda. Off = absent; old lomdas have none. */
export interface LomdaV3PlayerSettings {
    hidePageList?: boolean;
    navAfterScroll?: boolean;
}

export interface LomdaV3Metadata {
    numOfPages?: number;
    language: string;
    thumbnailUrl?: string;
    updatedAt?: string;
    theme?: LomdaTheme;
    fontWeights?: LomdaV3FontWeights;
    brandKitId?: string;
    style?: LomdaV3BrandStyle;
    aiInstructions?: string;
    assets?: LomdaV3Asset[];
    // Upload URL → the `uploadedAt` of the record deleted from the library.
    // Kept here, not on the record, so one undo brings the file back.
    deletedAssets?: Record<string, string>;
    player?: LomdaV3PlayerSettings;
}
